import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { DSSetup, PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import BlockFiltersComponent from 'webapp/components/block/filters/index';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import SubmitButtonComponent from 'webapp/components/submit-button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { FilterItem } from 'webapp/models-custom';

export default class CommonMasterFilterPopupComponent extends PopupInnerContextComponent<PopupData.Block.MasterFilter> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked isDimensionsLoading = false;
	@tracked isOperatorsLoading = false;

	@tracked editItem?: FilterItem;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		// make sure metric operators are loaded
		this.loadMetricOperators();

		this.loadMetricDimensions();

		if (this.allowAdvancedFilters) {
			// the metricSetup given will never have more than one filterItem for advanced
			// because we open modal in that case (see common/master-filter/index:openPopup)
			this.editItem = this.filterGroup?.filterItems[0];
		}
	}

	get metricSetup() {
		return this.popupData.metricSetup;
	}

	get metricDataItems(): DSSetup.MetricData[] {
		return this.metricSetup._metricData;
	}

	get allowAdvancedSetup() {
		return this.popupData.allowAdvancedSetup;
	}

	get allowAdvancedFilters() {
		return this.popupData.allowAdvancedFilters;
	}

	get isLoading() {
		return this.isDimensionsLoading;
	}

	private loadMetricDimensions() {
		this.isDimensionsLoading = true;

		void this.mmMetricsService
			.loadMasterFilterDimensions(this.metricSetup)
			.catch(() => {
				// show the form anyway
			})
			.finally(() => {
				this.isDimensionsLoading = false;
			});
	}

	private loadMetricOperators() {
		if (!this.allowAdvancedFilters) {
			return;
		}
		this.isOperatorsLoading = true;
		void this.mmMetricsService.getMetricOperatorLimitations(this.metricDataItems).then(() => {
			this.isOperatorsLoading = false;
		});
	}

	@action
	applyAdvanced(
		dimensions: string[] | undefined,
		filters: DSSetup.MetricSetupFiltersData | null,
	) {
		this.metricSetup.dimensions = dimensions || [];
		this.metricSetup.isUnspecificDimensionSelected = !dimensions;
		this.metricSetup.setFiltersFromData(filters);
		this.apply();
	}

	@action
	apply() {
		this.analyticsService.event(TrackingEvent.FILTER_DATA_SET, {
			origin: this.popupData.origin,
			filters: this.metricSetup.dimensionFilterItemsCount,
		});

		const dimensionToSave = this.metricSetup.isUnspecificDimensionSelected
			? undefined
			: this.metricSetup.dimensions;
		this.popupData.save(dimensionToSave, this.metricSetup.filtersData);
		this.close();
	}

	// ----- For setups that do not support advanced filters -----

	get filterGroup() {
		return this.metricSetup.dimensionFilterGroup;
	}

	get showClear() {
		return !!this.filterGroup && this.filterGroup?.filterItems.length > 0;
	}

	get showControls() {
		return (
			this.metricSetup.isDimensionSelected || !this.metricSetup.isUnspecificDimensionSelected
		);
	}

	get showAttributes() {
		return this.metricSetup.isDimensionSelected;
	}

	get dimensionOptions() {
		return this.metricSetup.metricDimensions.map((d: DSSetup.DimensionItem) => ({
			value: d.key,
			label: d.value,
		}));
	}

	@action
	changeDimensions(values: string[]) {
		// this method is for basic filters
		this.metricSetup.dimensions = values;
		this.metricSetup.resetFilters();
		this.scheduleReposition();
	}

	@action
	changeAttributeNames(values: string[]) {
		// this method is for basic filters
		this.metricSetup.setFiltersFromAttributeNames(values);
	}

	@action
	clearAndClose() {
		this.popupData.reset();
		this.close();
	}
	<template>
		<div class="ct">

			<header class="popup-header">
				<CommonUiIconComponent @name="Filter" @size="md" />
				Breakdown and filter data
			</header>

			{{#if this.isLoading}}
				<div class="loader-ct" {{willDestroy this.scheduleReposition}}>
					<BarLoaderComponent />
				</div>
			{{else}}

				{{#if this.allowAdvancedFilters}}

					<BlockFiltersComponent
						@popup={{@model}}
						@metricSetup={{this.metricSetup}}
						@close={{this.close}}
						@apply={{this.applyAdvanced}}
						@clearAndClose={{this.clearAndClose}}
						@editItem={{this.editItem}}
						@isInMasterContext={{this.allowAdvancedFilters}}
						@allowMultipleDimensions={{this.allowAdvancedSetup}}
						@theme={{@model.theme}}
						@onContentChange={{this.scheduleReposition}}
					/>

				{{else}}

					<div class="row" {{didInsert this.scheduleReposition}}>
						<label>Breakdown by</label>
						<FormSelectDimensionsComponent
							@metricSetup={{this.metricSetup}}
							@onChange={{this.changeDimensions}}
							@isMultiple={{this.allowAdvancedSetup}}
							@showFloatlabel={{false}}
							@theme={{@model.theme}}
						/>
					</div>

					{{#if this.showAttributes}}

						<div class="row row--basic-filter">
							<label>Filter</label>
							<span class="action-add action-add--disabled">
								Add more filters
								<CommonUiIconComponent @name="Plus" @size="sm" />
								<ToolTipComponent
									@text="Advanced filtering is available for dataset metrics"
									@triggerFunc="click"
									@maxWidth={{230}}
								/>
							</span>
							<FormSelectAttributesComponent
								@selected={{this.metricSetup.attributeNamesFromBasicFilters}}
								@metricSetupForQuery={{this.metricSetup}}
								@onChange={{this.changeAttributeNames}}
								@isMultiple={{true}}
								@showFloatlabel={{false}}
								@placeholder="Select"
								@maxSelectionLength={{15}}
								@theme={{@model.theme}}
							/>
						</div>

					{{/if}}

					{{#if this.showControls}}

						<div class="controls">
							{{#if this.showClear}}
								<a class="action-remove" {{on "click" this.clearAndClose}}>
									Remove
								</a>
							{{/if}}
							<SubmitButtonComponent
								@onTrigger={{this.apply}}
								@theme="blue"
								@text="Apply"
							/>
							<a class="cancel" {{on "click" this.close}}>Cancel</a>
						</div>

					{{/if}}

				{{/if}}

			{{/if}}
		</div>

		<a role="button" class="close" {{on "click" this.close}}>
			<CommonUiIconComponent @name="Close" @size="md" />
		</a>
	</template>
}
